<template>
  <div class="sms-page">
    <el-card class="tool-card" shadow="never">
      <div class="tool-row">
        <div class="tool-left">
          <span class="tool-title">短信群发</span>
          <span class="tool-sub">机构自行填写腾讯云短信配置，向逾期客户群发还款提醒</span>
        </div>
        <el-tag v-if="!smsEnabled" type="danger" size="small">短信模块已停用</el-tag>
        <el-tag v-else-if="configComplete" type="success" size="small">短信配置已就绪</el-tag>
        <el-tag v-else type="warning" size="small">请先填写短信配置</el-tag>
      </div>
    </el-card>

    <el-tabs v-model="tab" class="sms-tabs" @tab-click="onTab">
      <!-- 1. 短信配置（机构自填） -->
      <el-tab-pane label="短信配置" name="config">
        <el-card class="tool-card" shadow="never">
          <div class="seg-title">机构短信配置（腾讯云）</div>
          <div class="cfg-tip">填写后本机构即可使用自己的腾讯云短信账号下发还款提醒，无需购买短信套餐。</div>
          <el-form ref="cfgForm" :model="cfg" :rules="cfgRules" label-width="140px" size="small" style="max-width:640px">
            <el-form-item label="短信签名" prop="sign">
              <el-input v-model="cfg.sign" placeholder="如：XXXX还款提醒" :maxlength="100" />
            </el-form-item>
            <el-form-item label="腾讯云模板ID" prop="template">
              <el-input v-model="cfg.template" placeholder="如：1400xxxxxx" :maxlength="100" />
            </el-form-item>
            <el-form-item label="SecretId" prop="secret_id">
              <el-input v-model="cfg.secret_id" placeholder="腾讯云 SecretId" :maxlength="200" />
            </el-form-item>
            <el-form-item label="SecretKey" prop="secret_key">
              <el-input v-model="cfg.secret_key" :maxlength="200" placeholder="腾讯云 SecretKey" />
            </el-form-item>
            <el-form-item label="SmsSdkAppId" prop="sdk_app_id">
              <el-input v-model="cfg.sdk_app_id" :maxlength="50" placeholder="短信应用 SDKAppID" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" size="small" :loading="cfgSaving" @click="doSaveConfig">保存配置</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-tab-pane>

      <!-- 2. 逾期群发 -->
      <el-tab-pane label="逾期群发" name="send">
        <el-card class="tool-card" shadow="never">
          <el-form :inline="true" size="small" class="filter-form">
            <el-form-item label="姓名">
              <el-input v-model="filters.name" placeholder="客户姓名" clearable style="width:140px" @keyup.enter.native="loadOverdue" />
            </el-form-item>
            <el-form-item label="手机号">
              <el-input v-model="filters.phone" placeholder="手机号" clearable style="width:150px" @keyup.enter.native="loadOverdue" />
            </el-form-item>
            <el-form-item label="应还日">
              <el-date-picker v-model="filters.start_date" type="date" value-format="yyyy-MM-dd" placeholder="开始" style="width:130px" />
              <span class="range-sep">至</span>
              <el-date-picker v-model="filters.end_date" type="date" value-format="yyyy-MM-dd" placeholder="结束" style="width:130px" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-search" @click="loadOverdue">查询</el-button>
              <el-button type="success" icon="el-icon-position" class="send-btn" :disabled="!smsEnabled || !configComplete" @click="openCreate">群发短信</el-button>
            </el-form-item>
          </el-form>
          <div v-if="!smsEnabled" class="send-tip">短信模块已停用，请联系平台方</div>
          <div v-else-if="!configComplete" class="send-tip">短信配置未完成，请在「短信配置」页签填写签名 / 模板 / 腾讯云密钥</div>

          <el-table v-loading="overdueLoading" :data="overdueList" :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }" empty-text="暂无逾期客户">
            <el-table-column prop="customer_name" label="客户姓名" min-width="110" />
            <el-table-column prop="customer_phone" label="手机号" min-width="130" />
            <el-table-column label="期次" width="80" align="center">
              <template slot-scope="{ row }">{{ row.period_no }} 期</template>
            </el-table-column>
            <el-table-column label="应还金额" width="120" align="right">
              <template slot-scope="{ row }">¥ {{ fmtMoney(row.amount) }}</template>
            </el-table-column>
            <el-table-column prop="due_date" label="应还日" width="120" />
            <el-table-column label="逾期" width="90" align="center">
              <template slot-scope="{ row }"><span class="ov">{{ row.overdue_days }} 天</span></template>
            </el-table-column>
          </el-table>
          <div v-if="overdueTotal > 0" class="list-foot">本次筛选出 <b>{{ overdueTotal }}</b> 位逾期客户</div>
        </el-card>
      </el-tab-pane>

      <!-- 3. 任务记录 -->
      <el-tab-pane label="任务记录" name="tasks">
        <el-card class="tool-card" shadow="never">
          <div class="task-filter">
            <el-radio-group v-model="taskStatus" size="small" @change="loadTasks">
              <el-radio-button label="">全部</el-radio-button>
              <el-radio-button label="0">待发送</el-radio-button>
              <el-radio-button label="1">已发送</el-radio-button>
              <el-radio-button label="2">失败</el-radio-button>
            </el-radio-group>
          </div>
          <el-table v-loading="taskLoading" :data="tasks" :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }" empty-text="暂无发送任务">
            <el-table-column prop="title" label="任务名" min-width="140" />
            <el-table-column label="发送时间" min-width="150">
              <template slot-scope="{ row }">{{ fmtDateTime(row.send_time) }}</template>
            </el-table-column>
            <el-table-column label="数量" width="180">
              <template slot-scope="{ row }">
                <span class="t-total">{{ row.total }}</span> 条（成功 <span class="t-ok">{{ row.success }}</span> / 失败 <span class="t-fail">{{ row.fail }}</span>）
              </template>
            </el-table-column>
            <el-table-column label="状态" width="90" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="taskType(row.status)" size="small">{{ row.status_text }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="失败原因" min-width="140">
              <template slot-scope="{ row }">
                <span class="fail-reason">{{ row.fail_reason || '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90" align="center">
              <template slot-scope="{ row }">
                <el-button type="text" size="small" @click="openDetail(row)">明细</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- 创建群发任务弹窗 -->
    <el-dialog title="群发短信" :visible.sync="createVisible" width="440px">
      <div class="create-tip">本次将向当前筛选的 <b>{{ overdueTotal }}</b> 位逾期客户发送短信，使用本机构配置的签名与模板。</div>
      <el-form ref="createForm" :model="createForm" :rules="createRules" label-width="90px" size="small">
        <el-form-item label="任务名称" prop="title">
          <el-input v-model="createForm.title" :maxlength="100" />
        </el-form-item>
        <el-form-item label="发送时间">
          <el-radio-group v-model="sendMode">
            <el-radio label="now">立即发送</el-radio>
            <el-radio label="later">定时发送</el-radio>
          </el-radio-group>
          <div v-if="sendMode === 'later'" style="margin-top:8px">
            <el-date-picker v-model="createForm.send_time" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" placeholder="选择发送时间" style="width:100%" />
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="createVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="creating" @click="doCreate">发 送</el-button>
      </div>
    </el-dialog>

    <!-- 任务明细弹窗 -->
    <el-dialog :title="'发送明细 · ' + (detailTask.title || '')" :visible.sync="detailVisible" width="680px">
      <div class="detail-sum">
        共 {{ detailTask.total || 0 }} 条，成功 {{ detailTask.success || 0 }}，失败 {{ detailTask.fail || 0 }}
      </div>
      <el-table v-loading="detailLoading" :data="detailList" :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }" empty-text="暂无明细">
        <el-table-column prop="name" label="客户" min-width="100" />
        <el-table-column prop="phone" label="手机号" min-width="130" />
        <el-table-column label="结果" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="parseInt(row.status) ? 'success' : 'danger'" size="small">{{ parseInt(row.status) ? '成功' : '失败' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="fail_reason" label="失败原因" min-width="180" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { smsBalance, smsGetCompanyConfig, smsSaveCompanyConfig, smsOverdueList, smsCreate, smsTaskList, smsTaskDetail } from '@/api/sms'

export default {
  name: 'SmsSend',
  data() {
    return {
      tab: 'send',
      // 模块/配置状态
      configComplete: false,
      smsEnabled: true,
      // 机构短信配置
      cfg: { sign: '', template: '', secret_id: '', secret_key: '', sdk_app_id: '' },
      cfgSaving: false,
      cfgRules: {
        sign: [{ required: true, message: '请填写短信签名', trigger: 'blur' }],
        template: [{ required: true, message: '请填写腾讯云模板ID', trigger: 'blur' }],
        secret_id: [{ required: true, message: '请填写腾讯云 SecretId', trigger: 'blur' }],
        secret_key: [{ required: true, message: '请填写腾讯云 SecretKey', trigger: 'blur' }],
        sdk_app_id: [{ required: true, message: '请填写 SmsSdkAppId', trigger: 'blur' }]
      },
      // 群发
      filters: { name: '', phone: '', start_date: '', end_date: '' },
      overdueLoading: false,
      overdueList: [],
      overdueTotal: 0,
      createVisible: false,
      creating: false,
      sendMode: 'now',
      createForm: { title: '', send_time: '' },
      createRules: { title: [{ required: true, message: '请填写任务名称', trigger: 'blur' }] },
      // 任务
      taskStatus: '',
      taskLoading: false,
      tasks: [],
      detailVisible: false,
      detailLoading: false,
      detailList: [],
      detailTask: {}
    }
  },
  created() {
    this.loadBalance()
    this.loadConfig()
  },
  methods: {
    onTab(e) {
      if (e.name === 'send') this.loadOverdue()
      if (e.name === 'tasks') this.loadTasks()
    },
    /* ---- 状态/配置 ---- */
    loadBalance() {
      smsBalance()
        .then(data => {
          this.configComplete = !!data.config_complete
          this.smsEnabled = data.enabled !== false
        })
        .catch(() => {})
    },
    loadConfig() {
      smsGetCompanyConfig()
        .then(data => {
          this.cfg.sign = data.sign || ''
          this.cfg.template = data.template || ''
          this.cfg.secret_id = data.secret_id || ''
          this.cfg.sdk_app_id = data.sdk_app_id || ''
          this.configComplete = !!data.complete
          this.smsEnabled = data.enabled !== false
        })
        .catch(() => {})
    },
    doSaveConfig() {
      this.$refs.cfgForm.validate(valid => {
        if (!valid) return
        this.cfgSaving = true
        smsSaveCompanyConfig({ ...this.cfg })
          .then(() => {
            this.$message.success('短信配置已保存')
            this.loadBalance()
          })
          .catch(() => {})
          .finally(() => { this.cfgSaving = false })
      })
    },
    /* ---- 群发 ---- */
    loadOverdue() {
      this.overdueLoading = true
      smsOverdueList({
        name: this.filters.name,
        phone: this.filters.phone,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        limit: 100
      })
        .then(data => {
          this.overdueList = data.list || []
          this.overdueTotal = data.total || 0
        })
        .catch(() => { this.overdueList = []; this.overdueTotal = 0 })
        .finally(() => { this.overdueLoading = false })
    },
    openCreate() {
      this.createForm = { title: '', send_time: '' }
      this.sendMode = 'now'
      this.createVisible = true
    },
    doCreate() {
      this.$refs.createForm.validate(valid => {
        if (!valid) return
        if (this.sendMode === 'later' && !this.createForm.send_time) {
          this.$message.warning('请选择发送时间')
          return
        }
        this.creating = true
        smsCreate({
          title: this.createForm.title,
          send_time: this.sendMode === 'later' ? this.createForm.send_time : '',
          name: this.filters.name,
          phone: this.filters.phone,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date
        })
          .then(() => {
            this.$message.success('任务已创建')
            this.createVisible = false
            this.loadTasks()
            this.loadOverdue()
            this.loadBalance()
          })
          .catch(() => {})
          .finally(() => { this.creating = false })
      })
    },
    /* ---- 任务 ---- */
    loadTasks() {
      this.taskLoading = true
      smsTaskList({ status: this.taskStatus })
        .then(data => { this.tasks = data.list || [] })
        .catch(() => { this.tasks = [] })
        .finally(() => { this.taskLoading = false })
    },
    openDetail(row) {
      this.detailTask = row
      this.detailVisible = true
      this.detailLoading = true
      smsTaskDetail({ task_id: row.id })
        .then(data => { this.detailList = data.list || [] })
        .catch(() => { this.detailList = [] })
        .finally(() => { this.detailLoading = false })
    },
    /* ---- 工具 ---- */
    taskType(s) {
      s = parseInt(s)
      if (s === 1) return 'success'
      if (s === 2) return 'danger'
      return 'warning'
    },
    fmtMoney(n) {
      return parseFloat(n || 0).toFixed(2)
    },
    fmtDateTime(ts) {
      if (!ts) return '—'
      const d = new Date(parseInt(ts) * 1000)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.sms-page { padding: 0; }
.tool-card { margin-bottom: 14px; border-radius: 10px; border: 1px solid #E5E9F2; }
.tool-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.tool-left { display: flex; align-items: baseline; gap: 12px; }
.tool-title { font-size: 15px; font-weight: 700; color: #18233C; }
.tool-sub { font-size: 12px; color: #8A94AD; }

.cfg-tip { color: #8A94AD; font-size: 12px; margin-bottom: 14px; }

.seg-title { font-size: 14px; font-weight: 700; color: #18233C; margin-bottom: 14px; }

.filter-form { margin-bottom: 8px; }
.range-sep { padding: 0 6px; color: #8A94AD; }
.send-btn { margin-left: 6px; }
.send-tip { color: #E8833A; font-size: 12px; margin-bottom: 10px; }
.ov { color: #E5484D; }
.list-foot { margin-top: 10px; color: #8A94AD; font-size: 12px; }
.task-filter { margin-bottom: 12px; }
.t-total { font-weight: 700; color: #18233C; }
.t-ok { color: #34A46F; font-weight: 700; }
.t-fail { color: #E5484D; font-weight: 700; }
.fail-reason { color: #8A94AD; font-size: 12px; }
.detail-sum { color: #5A6580; font-size: 13px; margin-bottom: 12px; }
.create-tip { color: #8A94AD; font-size: 13px; margin-bottom: 14px; }

::v-deep .el-button--primary { background: #3668FC; border-color: #3668FC; }
::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }
::v-deep .el-table__row:hover > td { background: #F5F8FF !important; }
::v-deep .el-dialog { border-radius: 12px; }
</style>